import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { buddhismAPI } from '../utils/api';

const TempleAdminList = () => {
  const navigate = useNavigate();
  const [temples, setTemples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState(null);

  const fetchTemples = async (q = '') => {
    setLoading(true);
    try {
      const params = { limit: 100, is_admin: true };
      if (q) params.search = q;
      const res = await buddhismAPI.getAll(params);
      setTemples(res.data.results || res.data);
    } catch {
      setError('목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemples(); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTemples(search);
  };

  const handleDelete = async (temple) => {
    if (!window.confirm(`"${temple.name}"을(를) 삭제하시겠습니까?`)) return;
    setDeleting(temple.id);
    try {
      await buddhismAPI.delete(temple.id);
      setTemples(p => p.filter(t => t.id !== temple.id));
    } catch {
      alert('삭제 중 오류가 발생했습니다.');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="tal-wrap">
      {/* 헤더 */}
      <div className="tal-hero">
        <div className="tal-hero-bg">☸</div>
        <Container className="tal-hero-inner">
          <div className="tal-tabs">
            <button className="tal-tab active">사찰 관리</button>
            <button className="tal-tab" onClick={() => navigate('/admin/orders')}>주문 관리</button>
            <button className="tal-tab" onClick={() => navigate('/admin/orders?tab=settlements')}>정산 관리</button>
            <button className="tal-tab" onClick={() => navigate('/admin/users')}>유저 관리</button>
          </div>
          <h1 className="tal-hero-title">사찰 정보 관리</h1>
          <p className="tal-hero-sub">전체 사찰 목록을 조회하고 상세 정보를 수정합니다.</p>
        </Container>
      </div>

      <Container className="tal-container">
        {/* 툴바 */}
        <div className="tal-toolbar">
          <form onSubmit={handleSearch} className="tal-search-form">
            <input
              className="tal-search-input"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="사찰명, 지역, 주소 검색…"
            />
            <button type="submit" className="tal-search-btn">검색</button>
            {search && (
              <button type="button" className="tal-search-clear" onClick={() => { setSearch(''); fetchTemples(); }}>✕</button>
            )}
          </form>
          <button className="tal-btn tal-btn--primary" onClick={() => navigate('/admin/temples/write')}>
            + 사찰 등록
          </button>
        </div>

        {error && <div className="tal-alert">{error}</div>}

        {/* 테이블 */}
        {loading ? (
          <div className="tal-loading"><span className="tal-spin">☸</span><p>불러오는 중…</p></div>
        ) : temples.length === 0 ? (
          <div className="tal-empty">
            <span>🏯</span>
            <p>사찰이 없습니다. 새 사찰을 등록해주세요.</p>
          </div>
        ) : (
          <div className="tal-table-wrap">
            <table className="tal-table">
              <thead>
                <tr>
                  <th>사찰명</th>
                  <th>지역</th>
                  <th>주소</th>
                  <th>상태</th>
                  <th>종파</th>
                  <th>이미지</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {temples.map(t => (
                  <tr key={t.id}>
                    <td>
                      <strong className="tal-temple-name">{t.name}</strong>
                    </td>
                    <td>
                      {t.region ? <span className="tal-tag">{t.region}</span> : <span className="tal-na">-</span>}
                    </td>
                    <td className="tal-addr">{t.address || '-'}</td>
                    <td>
                      {t.is_preparing 
                        ? <span className="tal-tag" style={{background:'#fff3cd', color:'#856404'}}>준비중</span>
                        : <span className="tal-tag" style={{background:'#d4edda', color:'#155724'}}>운영중</span>}
                    </td>
                    <td>
                      {t.sect
                        ? <span className="tal-sect" style={{ borderColor: t.sect.color, color: t.sect.color }}>{t.sect.name}</span>
                        : <span className="tal-na">-</span>}
                    </td>
                    <td>
                      {t.images && t.images.length > 0
                        ? <img src={t.images[0].url} alt="" className="tal-thumb" />
                        : <span className="tal-na">-</span>}
                    </td>
                    <td>
                      <div className="tal-actions">
                        <button 
                          className="tal-act-btn tal-act-btn--orders" 
                          onClick={() => navigate(`/admin/orders?storeId=${t.id}&storeName=${encodeURIComponent(t.name)}`)}
                        >
                          주문
                        </button>
                        <button className="tal-act-btn tal-act-btn--edit" onClick={() => navigate(`/admin/temples/edit/${t.id}`)}>수정</button>
                        <button
                          className="tal-act-btn tal-act-btn--del"
                          onClick={() => handleDelete(t)}
                          disabled={deleting === t.id}
                        >
                          {deleting === t.id ? '…' : '삭제'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="tal-count">{!loading && `총 ${temples.length}개 사찰`}</div>
      </Container>

      <style>{`
        :root {
          --tal-teal:#009590; --tal-dark:#007873; --tal-pale:#e8f5f5;
          --tal-text:#2c3e35; --tal-muted:#6e8a84; --tal-border:#e4edec;
          --tal-red:#e74c3c;
        }
        .tal-wrap { min-height:100vh; background:#f8fbfb; }
        .tal-hero {
          position:relative; background:linear-gradient(150deg,#f0fafa,#d5efee);
          padding:52px 0 40px; text-align:center; overflow:hidden;
        }
        .tal-hero-bg {
          position:absolute; font-size:16rem; color:var(--tal-teal); opacity:.04;
          top:50%; left:50%; transform:translate(-50%,-50%); pointer-events:none;
        }
        .tal-hero-inner { position:relative; z-index:2; text-align: left; }
        .tal-tabs { display: flex; gap: 4px; margin-bottom: 24px; }
        .tal-tab { border: none; background: rgba(0,0,0,0.05); padding: 8px 20px; border-radius: 4px; font-size: 0.88rem; color: var(--tal-muted); font-weight: 600; cursor: pointer; transition: all 0.2s; }
        .tal-tab.active { background: var(--tal-teal); color: #fff; }
        .tal-hero-title { font-size:2rem; font-weight:700; color:var(--tal-teal); margin:6px 0 8px; }
        .tal-hero-sub { font-size:.9rem; color:var(--tal-muted); margin:0; }
        .tal-container { padding-top:32px; padding-bottom:80px; }
        .tal-toolbar { display:flex; align-items:center; gap:12px; margin-bottom:20px; flex-wrap:wrap; }
        .tal-search-form { display:flex; gap:6px; flex:1; min-width:220px; }
        .tal-search-input {
          flex:1; border:1px solid var(--tal-border); border-radius:4px;
          padding:10px 13px; font-size:.9rem; outline:none;
          transition:border-color .2s;
        }
        .tal-search-input:focus { border-color:var(--tal-teal); }
        .tal-search-btn {
          padding:10px 18px; background:var(--tal-teal); color:#fff; border:none;
          border-radius:4px; font-size:.88rem; font-weight:600; cursor:pointer;
          transition:background .2s;
        }
        .tal-search-btn:hover { background:var(--tal-dark); }
        .tal-search-clear {
          padding:10px 12px; background:#f3f4f6; border:none; border-radius:4px;
          cursor:pointer; color:var(--tal-muted); font-size:.85rem;
        }
        .tal-btn {
          padding:10px 20px; border-radius:4px; font-size:.9rem; font-weight:600;
          cursor:pointer; border:none; transition:all .2s; white-space:nowrap;
          font-family:'Noto Sans KR',sans-serif;
        }
        .tal-btn--primary { background:var(--tal-teal); color:#fff; }
        .tal-btn--primary:hover { background:var(--tal-dark); }
        .tal-alert {
          padding:12px 16px; background:#fff8f8; border-left:3px solid var(--tal-red);
          border-radius:4px; color:#c0392b; font-size:.88rem; margin-bottom:16px;
        }
        .tal-loading { text-align:center; padding:80px 0; color:var(--tal-muted); }
        .tal-spin { font-size:2rem; display:block; animation:talSpin 4s linear infinite; color:var(--tal-teal); opacity:.3; margin-bottom:12px; }
        @keyframes talSpin { to { transform:rotate(360deg); } }
        .tal-empty { text-align:center; padding:80px 0; color:var(--tal-muted); }
        .tal-empty span { font-size:2.5rem; display:block; margin-bottom:12px; }
        .tal-table-wrap { overflow-x:auto; border-radius:8px; border:1px solid var(--tal-border); }
        .tal-table { width:100%; border-collapse:collapse; background:#fff; }
        .tal-table th {
          padding:12px 16px; text-align:left; font-size:.8rem; font-weight:700;
          color:var(--tal-muted); text-transform:uppercase; letter-spacing:.06em;
          background:#f8fbfb; border-bottom:1px solid var(--tal-border);
        }
        .tal-table td {
          padding:12px 16px; border-bottom:1px solid var(--tal-border);
          font-size:.88rem; color:var(--tal-text); vertical-align:middle;
        }
        .tal-table tr:last-child td { border-bottom:none; }
        .tal-table tr:hover td { background:#f8fbfb; }
        .tal-temple-name { font-weight:600; }
        .tal-tag {
          display:inline-block; padding:2px 8px; background:var(--tal-pale);
          color:var(--tal-teal); border-radius:3px; font-size:.78rem; font-weight:600;
        }
        .tal-sect {
          display:inline-block; padding:2px 8px; border:1px solid; border-radius:3px;
          font-size:.78rem; font-weight:600;
        }
        .tal-na { color:#ccc; font-size:.85rem; }
        .tal-addr { color:var(--tal-muted); font-size:.82rem; max-width:200px; }
        .tal-thumb { width:44px; height:44px; object-fit:cover; border-radius:4px; border:1px solid var(--tal-border); }
        .tal-actions { display:flex; gap:6px; }
        .tal-act-btn {
          padding:5px 12px; border-radius:3px; font-size:.78rem; font-weight:600;
          cursor:pointer; transition:all .2s; border:1px solid;
        }
        .tal-act-btn--edit { border-color:var(--tal-teal); color:var(--tal-teal); background:none; }
        .tal-act-btn--edit:hover { background:var(--tal-pale); }
        .tal-act-btn--orders { border-color:#1890ff; color:#1890ff; background:none; }
        .tal-act-btn--orders:hover { background:#e6f7ff; }
        .tal-act-btn--del { border-color:var(--tal-red); color:var(--tal-red); background:none; }
        .tal-act-btn--del:hover { background:#fff8f8; }
        .tal-act-btn:disabled { opacity:.4; cursor:not-allowed; }
        .tal-count { margin-top:14px; font-size:.82rem; color:var(--tal-muted); text-align:right; }
        @media(max-width:640px) {
          .tal-toolbar { flex-direction:column; align-items:stretch; }
          .tal-btn { width:100%; text-align:center; }
        }
      `}</style>
    </div>
  );
};

export default TempleAdminList;
